import { useState, useEffect } from 'react';
import { platformApi } from '@/services/platformApi';

export interface BrandTheme {
  name: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  logoUrl: string;
  companyName: string;
  brandColors: {
    primary: string;
    secondary: string;
    accent: string;
    success: string;
    warning: string;
    error: string;
    info: string;
  };
  gradients: {
    primary: string;
    secondary: string;
    hero: string;
  };
}

export type ThemeMode = 'full' | 'subtle';

export interface BrandThemeConfig {
  theme: BrandTheme;
  mode: ThemeMode;
}

const brandThemes: Record<string, BrandTheme> = {
  clean: {
    name: 'Clean',
    primaryColor: '#1F2937',
    secondaryColor: '#F3F4F6',
    accentColor: '#F3F4F6',
    backgroundColor: '#FFFFFF',
    textColor: '#1F2937',
    logoUrl: '/favicon.ico',
    companyName: 'Vaiu',
    brandColors: {
      primary: '#1F2937',      // Clean dark grey (default theme)
      secondary: '#F3F4F6',    // Light grey
      accent: '#F3F4F6',       // Light grey
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
    },
    gradients: {
      primary: 'from-slate-800 to-slate-900',
      secondary: 'from-slate-50 to-slate-100',
      hero: 'from-slate-50 to-slate-100',
    },
  },
  vaiu: {
    name: 'Vaiu',
    primaryColor: '#1F2937',
    secondaryColor: '#F3F4F6',
    accentColor: '#F3F4F6',
    backgroundColor: '#FFFFFF',
    textColor: '#1F2937',
    logoUrl: '/img/logo.png',
    companyName: 'Vaiu Global Inc.',
    brandColors: {
      primary: '#1F2937',
      secondary: '#F3F4F6',
      accent: '#F3F4F6',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
    },
    gradients: {
      primary: 'from-slate-800 to-slate-900',
      secondary: 'from-slate-50 to-slate-100',
      hero: 'from-slate-50 to-slate-100',
    },
  },
  rbko: {
    name: 'RBKo',
    primaryColor: '#FFEB3B',
    secondaryColor: '#000000',
    accentColor: '#FFC107',
    backgroundColor: '#FAFAFA',
    textColor: '#000000', // Black text for readability
    logoUrl: '/rbko-logo.png',
    companyName: 'Raiffeisen Bank Kosovo',
    brandColors: {
      primary: '#FFEB3B', // Raiffeisen bright yellow
      secondary: '#000000', // Black for readability
      accent: '#FFC107', // Amber accent
      success: '#22C55E',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#FFEB3B',
    },
    gradients: {
      primary: 'from-yellow-400 to-yellow-500',
      secondary: 'from-gray-50 to-gray-100',
      hero: 'from-yellow-50 to-yellow-100',
    },
  },
  bnp: {
    name: 'BNP',
    primaryColor: '#00A651',
    secondaryColor: '#003D82',
    accentColor: '#FFD700',
    backgroundColor: '#F8FAFC',
    textColor: '#1E293B',
    logoUrl: '/img/banks/bnp-logo.png',
    companyName: 'BNP Paribas',
    brandColors: {
      primary: '#00A651',
      secondary: '#003D82',
      accent: '#FFD700',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
    },
    gradients: {
      primary: 'from-green-600 to-green-700',
      secondary: 'from-slate-50 to-green-50',
      hero: 'from-green-50 to-blue-50',
    },
  },
  ing: {
    name: 'ING',
    primaryColor: '#FF6200',
    secondaryColor: '#525199',
    accentColor: '#FF6200',
    backgroundColor: '#F8FAFC',
    textColor: '#1E293B',
    logoUrl: '/img/banks/ing-logo.png',
    companyName: 'ING',
    brandColors: {
      primary: '#FF6200',
      secondary: '#525199',
      accent: '#FF6200',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
    },
    gradients: {
      primary: 'from-orange-500 to-orange-600',
      secondary: 'from-slate-50 to-orange-50',
      hero: 'from-orange-50 to-slate-50',
    },
  },
  payformyfood: {
    name: 'PayForMyFood',
    primaryColor: '#DC2626',
    secondaryColor: '#991B1B',
    accentColor: '#F59E0B',
    backgroundColor: '#FEF2F2',
    textColor: '#1F2937',
    logoUrl: '/payformyfood-logo.png',
    companyName: 'Pay For My Food',
    brandColors: {
      primary: '#DC2626',
      secondary: '#991B1B',
      accent: '#F59E0B',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
    },
    gradients: {
      primary: 'from-red-600 to-red-700',
      secondary: 'from-red-50 to-orange-50',
      hero: 'from-red-50 to-yellow-50',
    },
  },
};

export const useBrandTheme = () => {
  const [currentBrand, setCurrentBrand] = useState<string>('clean');
  const [theme, setTheme] = useState<BrandTheme>(brandThemes.clean);
  const [themeMode, setThemeMode] = useState<ThemeMode>('full');

  useEffect(() => {
    // Check domain, environment, and URL parameters for brand and theme mode
    const updateBrandFromURL = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const brandParam = urlParams.get('brand');
      const themeModeParam = urlParams.get('theme-mode') as ThemeMode;
      
      let finalBrand = 'clean'; // Default to clean theme
      
      // Priority order:
      // 1. URL parameter (highest - for sharing/testing)
      // 2. User profile preference (high - user's saved choice)
      // 3. localStorage fallback (medium - temporary storage)
      // 4. Domain-based logo detection (medium - admin/deployment settings)
      // 5. Environment variable (low - build-time setting)
      // 6. Default fallback (lowest - clean theme)
      
      if (brandParam && brandThemes[brandParam.toLowerCase()]) {
        finalBrand = brandParam.toLowerCase();
        console.log('🎨 URL parameter brand selection:', finalBrand);
      } else {
        // Check user profile for theme preference
        try {
          const authToken = localStorage.getItem('authToken');
          // Check if user is a guest by decoding the JWT token
          let isGuestUser = false;
          if (authToken) {
            try {
              const tokenParts = authToken.split('.');
              if (tokenParts.length === 3) {
                const payload = JSON.parse(atob(tokenParts[1]));
                isGuestUser = payload.isGuest === true;
                if (isGuestUser) {
                  console.log('🎨 Guest user detected - will attempt preferences fetch');
                }
              }
            } catch (e) {
              console.warn('🎨 Could not decode token to check guest status');
            }
          }
          
          if (authToken) {
            // Try to fetch preferences for all authenticated users (including guests)
            // Guest users should have full functionality
            console.log('🎨 Authenticated user detected, fetching theme preferences');
            // Use 'theme' preference type (not 'brand-theme')
            const themePrefs = await platformApi.getPreferences('theme' as any);
            if (themePrefs.themePreferences?.selectedTheme && brandThemes[themePrefs.themePreferences.selectedTheme]) {
              finalBrand = themePrefs.themePreferences.selectedTheme;
              console.log('🎨 User profile theme preference:', finalBrand);
            } else {
              // Fallback to localStorage for backwards compatibility
              const savedBrand = localStorage.getItem('vaiu-brand-preference');
              if (savedBrand && brandThemes[savedBrand.toLowerCase()]) {
                finalBrand = savedBrand.toLowerCase();
                console.log('🎨 localStorage brand selection:', finalBrand);
              } else {
                // Domain-based detection affects logo but not theme (admin settings)
                const hostname = window.location.hostname;
                let domainBrand = 'clean'; // Default for logo domain detection
                
                if (hostname.includes('rbko.vaiu.app')) {
                  domainBrand = 'rbko';
                  console.log('🎨 Domain-based logo detection: RBKo');
                } else if (hostname.includes('bnp.banks.vaiu.app')) {
                  domainBrand = 'bnp';
                  console.log('🎨 Domain-based logo detection: BNP');
                } else if (hostname.includes('ing.banks.vaiu.app')) {
                  domainBrand = 'ing';
                  console.log('🎨 Domain-based logo detection: ING');
                } else if (hostname.includes('payformyfood.vaiu.app')) {
                  domainBrand = 'payformyfood';
                  console.log('🎨 Domain-based logo detection: PayForMyFood');
                } else {
                  // For all other domains, use clean theme unless environment variable is set
                  const envBrand = (import.meta as any).env?.VITE_DEFAULT_BRAND;
                  if (envBrand && brandThemes[envBrand.toLowerCase()]) {
                    domainBrand = envBrand.toLowerCase();
                    console.log('🎨 Environment-based brand detection:', domainBrand);
                  } else {
                    console.log('🎨 Using clean theme (default)');
                  }
                }
                
                finalBrand = domainBrand;
              }
            }
          } else {
            // No auth token, use domain-based detection
            const hostname = window.location.hostname;
            if (hostname.includes('rbko.vaiu.app')) {
              finalBrand = 'rbko';
              console.log('🎨 Domain-based brand detection: RBKo');
            } else if (hostname.includes('bnp.banks.vaiu.app')) {
              finalBrand = 'bnp';
              console.log('🎨 Domain-based brand detection: BNP');
            } else if (hostname.includes('ing.banks.vaiu.app')) {
              finalBrand = 'ing';
              console.log('🎨 Domain-based brand detection: ING');
            } else if (hostname.includes('payformyfood.vaiu.app')) {
              finalBrand = 'payformyfood';
              console.log('🎨 Domain-based brand detection: PayForMyFood');
            } else {
              const envBrand = (import.meta as any).env?.VITE_DEFAULT_BRAND;
              if (envBrand && brandThemes[envBrand.toLowerCase()]) {
                finalBrand = envBrand.toLowerCase();
                console.log('🎨 Environment-based brand detection:', finalBrand);
              } else {
                console.log('🎨 Using clean theme (default)');
              }
            }
          }
        } catch (error) {
          console.warn('🎨 Failed to load user theme preferences:', error);
          // Fallback to localStorage
          const savedBrand = localStorage.getItem('vaiu-brand-preference');
          if (savedBrand && brandThemes[savedBrand.toLowerCase()]) {
            finalBrand = savedBrand.toLowerCase();
            console.log('🎨 localStorage fallback brand selection:', finalBrand);
          } else {
            // Domain-based detection
            const hostname = window.location.hostname;
            if (hostname.includes('rbko.vaiu.app')) {
              finalBrand = 'rbko';
              console.log('🎨 Domain-based brand detection: RBKo');
            } else if (hostname.includes('bnp.banks.vaiu.app')) {
              finalBrand = 'bnp';
              console.log('🎨 Domain-based brand detection: BNP');
            } else if (hostname.includes('ing.banks.vaiu.app')) {
              finalBrand = 'ing';
              console.log('🎨 Domain-based brand detection: ING');
            } else if (hostname.includes('payformyfood.vaiu.app')) {
              finalBrand = 'payformyfood';
              console.log('🎨 Domain-based brand detection: PayForMyFood');
            } else {
              const envBrand = (import.meta as any).env?.VITE_DEFAULT_BRAND;
              if (envBrand && brandThemes[envBrand.toLowerCase()]) {
                finalBrand = envBrand.toLowerCase();
                console.log('🎨 Environment-based brand detection:', finalBrand);
              } else {
                console.log('🎨 Using clean theme (default)');
              }
            }
          }
        }
      }
      
      console.log('🎨 Final brand selection:', finalBrand);
      
      // Check localStorage for theme mode preference
      const savedThemeMode = localStorage.getItem('vaiu-theme-mode-preference') as ThemeMode;
      const initialMode = themeModeParam || savedThemeMode || 'full';
      setThemeMode(initialMode);
      
      if (brandThemes[finalBrand]) {
        console.log('🎨 Applying brand theme:', finalBrand, 'mode:', initialMode);
        setCurrentBrand(finalBrand);
        setTheme(brandThemes[finalBrand]);
        
        // Apply CSS custom properties for the brand theme
        if (finalBrand === 'clean') {
          // For clean theme, reset to original CSS variables
          resetToCleanTheme();
        } else {
          applyBrandTheme(brandThemes[finalBrand], finalBrand, initialMode);
        }
      } else {
        // Fallback to clean theme
        console.log('🎨 Fallback to clean theme with mode:', initialMode);
        setCurrentBrand('clean');
        setTheme(brandThemes.clean);
        resetToCleanTheme();
      }
    };

    // Initial check
    updateBrandFromURL();

    // Listen for URL changes (for single-page app navigation)
    const handlePopState = () => {
      updateBrandFromURL();
    };

    window.addEventListener('popstate', handlePopState);
    
    // Also listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      setTimeout(() => updateBrandFromURL(), 0);
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      setTimeout(() => updateBrandFromURL(), 0);
    };

    return () => {
      window.removeEventListener('popstate', handlePopState);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  const resetToCleanTheme = () => {
    console.log('🎨 Resetting to clean theme');
    const root = document.documentElement;
    
    // Reset to default theme values
    root.style.setProperty('--background', '0 0% 100%');
    root.style.setProperty('--foreground', '222.2 84% 4.9%');
    root.style.setProperty('--primary', '222.2 47.4% 11.2%');
    root.style.setProperty('--primary-foreground', '210 40% 98%');
    root.style.setProperty('--secondary', '210 40% 96.1%');
    root.style.setProperty('--secondary-foreground', '222.2 47.4% 11.2%');
    root.style.setProperty('--muted', '210 40% 96.1%');
    root.style.setProperty('--muted-foreground', '215.4 16.3% 46.9%');
    root.style.setProperty('--accent', '210 40% 96.1%');
    root.style.setProperty('--accent-foreground', '222.2 47.4% 11.2%');
    root.style.setProperty('--destructive', '0 84.2% 60.2%');
    root.style.setProperty('--destructive-foreground', '210 40% 98%');
    root.style.setProperty('--border', '214.3 31.8% 91.4%');
    root.style.setProperty('--input', '214.3 31.8% 91.4%');
    root.style.setProperty('--ring', '222.2 84% 4.9%');
    root.style.setProperty('--success', '142 76% 36%');
    root.style.setProperty('--warning', '38 92% 50%');
    root.style.setProperty('--error', '0 84% 60%');
    root.style.setProperty('--info', '221 83% 53%');
    // Surface tokens for portaled Radix UI (dropdowns, dialogs)
    root.style.setProperty('--popover', '0 0% 100%');
    root.style.setProperty('--popover-foreground', '222.2 84% 4.9%');
    root.style.setProperty('--card', '0 0% 100%');
    root.style.setProperty('--card-foreground', '222.2 84% 4.9%');
    
    // Remove brand-specific CSS classes
    document.body.className = document.body.className.replace(/brand-\w+/g, '').replace(/theme-\w+/g, '');
    document.body.classList.add('brand-clean', 'theme-full');
    
    // Reset page title
    document.title = 'Vaiu - Digital Payment Platform';
    
    // Reset favicon
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon) {
      favicon.href = '/favicon.ico';
    }
    
    // Reset meta theme-color
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.setAttribute('name', 'theme-color');
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.setAttribute('content', '#1F2937');
  };

  const applyBrandTheme = (brandTheme: BrandTheme, brandKey: string, mode: ThemeMode = 'full') => {
    console.log('🎨 Applying brand theme:', brandKey, brandTheme, 'mode:', mode);
    
    const root = document.documentElement;
    
    // Convert hex colors to HSL for CSS custom properties
    const hexToHsl = (hex: string) => {
      const r = parseInt(hex.slice(1, 3), 16) / 255;
      const g = parseInt(hex.slice(3, 5), 16) / 255;
      const b = parseInt(hex.slice(5, 7), 16) / 255;
      
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0, s = 0, l = (max + min) / 2;
      
      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break;
          case g: h = (b - r) / d + 2; break;
          case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
      }
      
      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    // Apply brand colors based on theme mode
    console.log('🎨 Setting CSS custom properties for brand:', brandKey);
    
    if (mode === 'subtle') {
      // In subtle mode, keep neutral colors for UI but set brand colors for borders
      root.style.setProperty('--primary', '220 13% 69%'); // Neutral gray
      root.style.setProperty('--secondary', '215 28% 17%'); // Dark gray
      root.style.setProperty('--accent', '220 13% 69%'); // Neutral gray
      root.style.setProperty('--success', hexToHsl(brandTheme.brandColors.success));
      root.style.setProperty('--warning', hexToHsl(brandTheme.brandColors.warning));
      root.style.setProperty('--error', hexToHsl(brandTheme.brandColors.error));
      root.style.setProperty('--info', '220 13% 69%');
      
      // Keep neutral backgrounds
      root.style.setProperty('--background', '0 0% 100%'); // White
      root.style.setProperty('--foreground', '215 28% 17%'); // Dark gray
      root.style.setProperty('--popover', '0 0% 100%');
      root.style.setProperty('--popover-foreground', '215 28% 17%');
      root.style.setProperty('--card', '0 0% 100%');
      root.style.setProperty('--card-foreground', '215 28% 17%');
      
      // Set brand colors for borders and frames
      root.style.setProperty('--brand-primary', hexToHsl(brandTheme.brandColors.primary));
      root.style.setProperty('--brand-secondary', hexToHsl(brandTheme.brandColors.secondary));
      root.style.setProperty('--brand-accent', hexToHsl(brandTheme.brandColors.accent));
    } else {
      // Full mode - apply brand colors to everything
      root.style.setProperty('--primary', hexToHsl(brandTheme.brandColors.primary));
      root.style.setProperty('--secondary', hexToHsl(brandTheme.brandColors.secondary));
      root.style.setProperty('--accent', hexToHsl(brandTheme.brandColors.accent));
      root.style.setProperty('--success', hexToHsl(brandTheme.brandColors.success));
      root.style.setProperty('--warning', hexToHsl(brandTheme.brandColors.warning));
      root.style.setProperty('--error', hexToHsl(brandTheme.brandColors.error));
      root.style.setProperty('--info', hexToHsl(brandTheme.brandColors.info));
      
      // Apply background and text colors
      root.style.setProperty('--background', hexToHsl(brandTheme.backgroundColor));
      root.style.setProperty('--foreground', hexToHsl(brandTheme.textColor));
      root.style.setProperty('--popover', '0 0% 100%');
      root.style.setProperty('--popover-foreground', hexToHsl(brandTheme.textColor));
      root.style.setProperty('--card', '0 0% 100%');
      root.style.setProperty('--card-foreground', hexToHsl(brandTheme.textColor));
      
      // Set brand colors for borders and frames (same as theme colors in full mode)
      root.style.setProperty('--brand-primary', hexToHsl(brandTheme.brandColors.primary));
      root.style.setProperty('--brand-secondary', hexToHsl(brandTheme.brandColors.secondary));
      root.style.setProperty('--brand-accent', hexToHsl(brandTheme.brandColors.accent));
    }
    
    // Apply brand-specific CSS class to body
    console.log('🎨 Applying brand class to body:', `brand-${brandKey}`, `theme-${mode}`);
    document.body.className = document.body.className.replace(/brand-\w+/g, '').replace(/theme-\w+/g, '');
    document.body.classList.add(`brand-${brandKey}`, `theme-${mode}`);
    
    // Update page title and meta tags
    document.title = `${brandTheme.companyName} - Digital Payment Platform`;
    console.log('🎨 Updated page title:', document.title);
    
    // Update meta theme-color for mobile browsers
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.setAttribute('name', 'theme-color');
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.setAttribute('content', brandTheme.primaryColor);
    console.log('🎨 Updated meta theme-color:', brandTheme.primaryColor);
    
    // Update favicon based on brand
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon) {
      switch (brandKey) {
        case 'rbko':
          favicon.href = '/rbko-favicon.ico';
          break;
        case 'bnp':
          favicon.href = '/bnp-favicon.ico';
          break;
        case 'payformyfood':
          favicon.href = '/payformyfood-favicon.ico';
          break;
        case 'vaiu':
          favicon.href = '/favicon.ico';
          break;
        default:
          favicon.href = '/favicon.ico';
          break;
      }
    }
  };

  const switchBrand = async (brandKey: string, mode?: ThemeMode) => {
    if (brandThemes[brandKey]) {
      setCurrentBrand(brandKey);
      setTheme(brandThemes[brandKey]);
      const newMode = mode || themeMode;
      
      if (brandKey === 'clean') {
        resetToCleanTheme();
      } else {
        applyBrandTheme(brandThemes[brandKey], brandKey, newMode);
      }
      
      // Save user preference to profile (with localStorage fallback)
      try {
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
          // Use generic preferences API to save brand theme
          await platformApi.updatePreferences('theme' as any, {
            selectedTheme: brandKey,
            themeMode: newMode
          } as any);
          console.log('🎨 Saved user theme preference to profile:', brandKey, 'mode:', newMode);
        } else {
          // Fallback to localStorage if not authenticated
          localStorage.setItem('vaiu-brand-preference', brandKey);
          localStorage.setItem('vaiu-theme-mode-preference', newMode);
          console.log('🎨 Saved user theme preference to localStorage:', brandKey, 'mode:', newMode);
        }
      } catch (error) {
        console.warn('🎨 Failed to save theme preference to profile, using localStorage:', error);
        // Fallback to localStorage
        localStorage.setItem('vaiu-brand-preference', brandKey);
        localStorage.setItem('vaiu-theme-mode-preference', newMode);
        console.log('🎨 Saved user theme preference to localStorage (fallback):', brandKey, 'mode:', newMode);
      }
      
      // Update URL with brand parameter (for sharing/testing)
      const url = new URL(window.location.href);
      url.searchParams.set('brand', brandKey);
      url.searchParams.set('theme-mode', newMode);
      window.history.replaceState({}, '', url.toString());
    }
  };

  const switchThemeMode = (mode: ThemeMode) => {
    setThemeMode(mode);
    if (currentBrand === 'clean') {
      resetToCleanTheme();
    } else {
      applyBrandTheme(theme, currentBrand, mode);
    }
    
    // Save user preference to localStorage
    localStorage.setItem('vaiu-theme-mode-preference', mode);
    console.log('🎨 Saved user theme mode preference:', mode);
    
    // Update URL with theme mode parameter (for sharing/testing)
    const url = new URL(window.location.href);
    url.searchParams.set('theme-mode', mode);
    window.history.replaceState({}, '', url.toString());
  };

  const getAvailableBrands = () => {
    return Object.keys(brandThemes);
  };

  const resetToCleanThemeNow = () => {
    switchBrand('clean');
  };

  const clearUserPreferences = async () => {
    // Clear localStorage preferences
    localStorage.removeItem('vaiu-brand-preference');
    localStorage.removeItem('vaiu-theme-mode-preference');
    
    // Clear profile preferences
    try {
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        // Use generic preferences API to clear brand theme
        await platformApi.updatePreferences('brand-theme' as any, {
          selectedTheme: null,
          themeMode: 'full'
        } as any);
        console.log('🎨 Cleared user theme preferences from profile');
      }
    } catch (error) {
      console.warn('🎨 Failed to clear theme preferences from profile:', error);
    }
    
    console.log('🎨 Cleared user theme preferences');
    
    // Reload the page to re-detect theme based on domain/environment
    window.location.reload();
  };

  const getThemeSource = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const brandParam = urlParams.get('brand');
    const savedBrand = localStorage.getItem('vaiu-brand-preference');
    const hostname = window.location.hostname;
    const envBrand = (import.meta as any).env?.VITE_DEFAULT_BRAND;
    
    if (brandParam && brandThemes[brandParam.toLowerCase()]) {
      return 'URL parameter';
    } else if (savedBrand && brandThemes[savedBrand.toLowerCase()]) {
      return 'User preference (localStorage)';
    } else if (hostname.includes('rbko.vaiu.app') || hostname.includes('bnp.banks.vaiu.app') || hostname.includes('payformyfood.vaiu.app')) {
      return 'Domain-based detection';
    } else if (envBrand && brandThemes[envBrand.toLowerCase()]) {
      return 'Environment variable';
    } else {
      return 'Default fallback';
    }
  };

  return {
    currentBrand,
    theme,
    themeMode,
    switchBrand,
    switchThemeMode,
    getAvailableBrands,
    applyBrandTheme,
    resetToCleanTheme: resetToCleanThemeNow,
    clearUserPreferences,
    getThemeSource,
  };
}; 